import React, { useEffect, useState, createContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../store';
import { SEND_MONEY_STEPS, WIZARD_MODE_STEPS } from '../consts';
import { useSetLinkAccount } from '../../../hooks/query/useSetLinkAccount';
import { useGetLinkedAccountsQuery } from '../../../hooks/query/useGetLinkedAccountsQuery';
import { ILinkAccountProps, ILinkedAccount, IPostLinkData, IServerErrorResponse } from '../../../types';
import { SendMoneyIntro } from './SendMoneyIntro';
import FullPageLoader from '../../../components/FullPageLoader';
import PlaidLinkedAccounts from './PlaidLinkedAccounts';
import TransferAmountPage from './TransferAmountPage';
import { NavigationHeader } from '../../../components/NavigationHeader';
import { getPayeeDetailsText, getRowTitlesForSuccessPage, buildSuccessBoxTransferData } from '../utils';
import TransferReviewPage from './TransferReviewPage';
import { IFeeData } from '../../../store/createBankTransferSlice';
import { useNewSendMoneyMutation } from '../../../hooks/mutations/useNewSendMoneyMutation';
import { useUploadMoneyTransferConfirmation } from '../../../hooks/mutations/useUploadMoneyTransferConfirmation';
import bugsnagHandler from '../../../services/bugsnag.service';
import { takeSnapshot } from '../../../services/snapshot.service';
import { IAchOutResponse } from '../../../hooks/mutations/useSendAchOutMutation';
import { IAchOutLimits, useGetAchOutLimits } from '../../../hooks/query/useGetAchOutLimits';
import { getLowestLimit } from '../../../helpers/moneyTransfer.helper';
import { handleSendMoneyError } from '../services/SendMoneyErrorService';
import TransferSuccessPage from './TransferSuccessPage';
import SendMoneyLimitsError from './SendMoneyLimitsError';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { Layout } from '../../../components/Layout/Layout';
import Heading from '../../../generic-components/Heading';

export const STYLES = {
  TITLE: 'font-metricMedium text-xl',
  NEW_LAYOUT:
    'bg-bw02 flex flex-col tab-landscape:pl-[112px] pc-sm:pl-[320px] pt-[46px] pb-[24px] fadeIn tab-landscape:w-[calc(100%-34px)] pc-sm:w-[calc(100%-34px)] h-[100vh]',
  MAIN_CONTENT: 'relative flex flex-col bg-bw02 rounded-lg w-full h-full',
  WIZARD_MODE_LAYOUT: 'absolute inset-0 z-[50] bg-bw00 h-screen w-screen flex flex-col fadeIn overflow-hidden',
};

export const INITIAL_SEND_MONEY_RESPONSE = {
  processTime: '',
  status: '',
  transactionId: '',
};

const INITIAL_LIMITS = { key: '', value: 0 };

export const SendMoneyContext = createContext<any>(null);

const { LOADING, ENROLL_ERROR, INTRO, ACCOUNTS, AMOUNT, REVIEW, SUCCESS } = SEND_MONEY_STEPS;

export const SendMoney = () => {
  const [activeStep, setActiveStep] = useState<string>(LOADING);
  const { customerId } = useStore((state) => state.user);
  const { accountExternalId } = useStore((state) => state.getPersonalAccount());
  const { t } = useTranslation();
  const [linkAccountProps, setLinkAccountProps] = useState<ILinkAccountProps>({
    publicToken: '',
    institutionId: '',
    metaDataStr: '',
  });
  const [selectedLinkedAccount, setSelectedLinkedAccount] = useState<ILinkedAccount | null>(null);
  const [chosenFee, setChosenFee] = useState<IFeeData | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [snapshotFormData, setSnapShotFormData] = useState<unknown>();
  const [sendMoneyResponse, setSendMoneyResponse] = useState<IAchOutResponse>(INITIAL_SEND_MONEY_RESPONSE);
  const [lowestLimit, setLowestLimit] = useState<{ key: string; value: number }>(INITIAL_LIMITS);
  const [balance, setBalance] = useState<number>(0);
  const [enrollErrorData, setEnrollErrorData] = useState<{ errCode: number; errMsg: string }>({
    errCode: 0,
    errMsg: '',
  });
  const {
    data: updatedLinkedAccounts,
    refetch: getUpdatedLinkedAccounts,
    isRefetching: isReFetchingLinkedAccounts,
    isLoading: isLoadingGetLinkedAccounts,
  } = useGetLinkedAccountsQuery(customerId);
  const {
    refetch: setLinkAccount,
    data: linkAccountsResponse,
    isRefetching: isReFetchingSetLinkAccount,
    isLoading: isLoadingSetLinkAccount,
  } = useSetLinkAccount(customerId, linkAccountProps);
  const { refetch: getAchOutLimits } = useGetAchOutLimits(
    {
      customerId,
      accountExternalId,
    },
    (achLimitsResponse: IAchOutLimits) => {
      setLowestLimit(getLowestLimit(achLimitsResponse));
      setBalance(achLimitsResponse.balance);
      getUpdatedLinkedAccounts();
    },
    (errData: { errCode: number; errMsg: string }) => {
      setLowestLimit(INITIAL_LIMITS);
      setEnrollErrorData(errData);
      setActiveStep(SEND_MONEY_STEPS.ENROLL_ERROR);
    },
  );
  const handleSendMoneyMutationError = (type: string) => {
    if (type === 'modifyAmount') {
      setAmount('');
      setActiveStep(SEND_MONEY_STEPS.AMOUNT);
    }
  };
  const { mutate: sendMoney, isLoading: isSendMoneyLoading } = useNewSendMoneyMutation(
    {
      customerId,
      accountExternalId,
      linkedAccountId: selectedLinkedAccount?.externalId || '',
      amount,
      feeData: chosenFee,
    },
    (data: IAchOutResponse) => {
      setSendMoneyResponse(data);
    },
    (error: IServerErrorResponse) => {
      trackMixPanel('Error Occurred During Move Money Flow', {
        Flow: 'plaid transfer out',
        ErrorCode: error?.response?.data?.errors?.[0]?.code,
        errorMessage: error?.response?.data?.errors?.[0]?.localizedMessage ?? '',
        screenName: 'Review',
      });
      bugsnagHandler.notify(
        `Failed to Send Money, customerId: ${customerId} ErrorCode: ${error?.response?.data?.errors?.[0]?.code}`,
      );
      const EXPIRED_LINKED_TOKEN_ERRORS = [4252, 4253, 4255];
      const errorCode = error?.response?.data?.errors?.[0]?.code;
      if (EXPIRED_LINKED_TOKEN_ERRORS.includes(errorCode)) {
        setAmount('');
        setSelectedLinkedAccount(null);
        setChosenFee(null);
        setSendMoneyResponse(INITIAL_SEND_MONEY_RESPONSE);
        setActiveStep(SEND_MONEY_STEPS.INTRO);
      } else {
        handleSendMoneyError(error, balance, amount, chosenFee?.fee || 0, handleSendMoneyMutationError);
      }
    },
  );

  const { mutate: uploadScreenshot } = useUploadMoneyTransferConfirmation(
    {
      fileData: snapshotFormData,
      customerId,
      transactionId: sendMoneyResponse?.transactionId,
    },
    () => {
      setSnapShotFormData(null);
    },
    () => {
      bugsnagHandler.notify(`Failed to send a Send Money snapshot for customerId: ${customerId}`);
      setSnapShotFormData(null);
    },
  );

  const takeScreenShot = async () => {
    const formData: unknown = await takeSnapshot();
    setSnapShotFormData(formData);
  };

  const handleTakeScreenshot = async () => {
    await takeScreenShot();
  };

  const sendMoneyContextValue = useMemo(
    () => ({
      selectedLinkedAccount,
      amount,
      setAmount,
      chosenFee,
      setChosenFee,
      isSendMoneyLoading,
      sendMoneyResponse,
      setSendMoneyResponse,
      lowestLimit,
      balance,
    }),
    [selectedLinkedAccount, amount, chosenFee, isSendMoneyLoading, sendMoneyResponse, lowestLimit, balance],
  );

  const handlePlaidMetaData = (data: IPostLinkData) => {
    if (data) {
      setLinkAccountProps((prevState) => ({
        ...prevState,
        publicToken: data.publicToken,
        institutionId: data.metaData.institution!.institution_id,
        metaDataStr: JSON.stringify(data.metaData),
      }));
    }
  };

  useEffect(() => {
    if (!linkAccountsResponse) return;
    getUpdatedLinkedAccounts();
  }, [linkAccountsResponse]);

  useEffect(() => {
    if (!linkAccountProps.publicToken) return;
    setLinkAccount();
  }, [linkAccountProps]);

  useEffect(() => {
    if (
      sendMoneyResponse?.status === 'SUCCESS' &&
      sendMoneyResponse?.transactionId &&
      sendMoneyResponse?.processTime !== ''
    ) {
      if (activeStep === REVIEW) handleTakeScreenshot();
      setTimeout(() => {
        setActiveStep(SUCCESS);
      }, 300);
    }
  }, [sendMoneyResponse]);

  useEffect(() => {
    if (activeStep === SUCCESS) handleTakeScreenshot();
  }, [activeStep]);

  useEffect(() => {
    if (snapshotFormData) {
      uploadScreenshot();
    }
  }, [snapshotFormData]);
  useEffect(() => {
    if (typeof updatedLinkedAccounts !== 'undefined' && lowestLimit?.key !== '') {
      const hasAccounts = updatedLinkedAccounts?.accounts?.length > 0;
      if (hasAccounts && !amount && selectedLinkedAccount === null) setActiveStep(SEND_MONEY_STEPS.ACCOUNTS);
      else setActiveStep(SEND_MONEY_STEPS.INTRO);
    }
  }, [updatedLinkedAccounts, lowestLimit]);

  useEffect(() => {
    getAchOutLimits();

    return () => {
      setChosenFee(null);
    };
  }, []);

  const handleOnWizardCloseClick = () => {
    setAmount('');
    setSelectedLinkedAccount(null);
    setActiveStep(ACCOUNTS);
  };

  const handleAmountSubmit = (transferAmount: string) => {
    setAmount(transferAmount);
  };

  const handleEditAmountClick = () => {
    setAmount('');
  };
  const handleEditPayeeClick = () => {
    setSelectedLinkedAccount(null);
    setAmount('');
    setActiveStep(ACCOUNTS);
  };
  const handleMakeAnotherTransferClick = () => {
    setAmount('');
    setSelectedLinkedAccount(null);
    setChosenFee(null);
    setSendMoneyResponse(INITIAL_SEND_MONEY_RESPONSE);
    setActiveStep(SEND_MONEY_STEPS.LOADING);
    getAchOutLimits();
  };
  const isLoadingLinkedAccounts =
    isReFetchingLinkedAccounts || isReFetchingSetLinkAccount || isLoadingSetLinkAccount || isLoadingGetLinkedAccounts;
  const showWizardMode = WIZARD_MODE_STEPS.includes(activeStep);
  const isInErrorStep = activeStep === ENROLL_ERROR;
  const showAmountStep = selectedLinkedAccount !== null && !amount && activeStep !== AMOUNT && !isInErrorStep;
  const showReviewStep =
    selectedLinkedAccount !== null && amount && activeStep === AMOUNT && activeStep !== REVIEW && !isInErrorStep;
  if (showAmountStep) setActiveStep(AMOUNT);
  else if (showReviewStep) setActiveStep(REVIEW);
  return (
    <Layout
      shouldHideProfile={WIZARD_MODE_STEPS.includes(activeStep)}
      shouldHideChatBot={WIZARD_MODE_STEPS.includes(activeStep)}
      wrapperClassName='pt-[78px] pl-[72px] flex flex-col w-full h-full pr-[32px] pb-[20px] ml-[100px] pc-md:ml-[256px]'
    >
      <Heading
        title={showWizardMode || activeStep === ENROLL_ERROR ? '' : t('SEND_MONEY.MAIN_PAGE_TITLE')}
        className='font-metricRSemibold text-[20px] text-bw08 mb-[24px]'
      />
      <SendMoneyContext.Provider value={sendMoneyContextValue}>
        <div className={WIZARD_MODE_STEPS.includes(activeStep) ? STYLES.WIZARD_MODE_LAYOUT : STYLES.MAIN_CONTENT}>
          {WIZARD_MODE_STEPS.includes(activeStep) && (
            <NavigationHeader
              layoutStyle='absolute top-0 z-[99999] flex items-center justify-between w-full bg-bw00 border-b-[1px] border-bw03 px-[34px] py-[17px]'
              onCloseAction={handleOnWizardCloseClick}
              pageTitle=''
            />
          )}

          {activeStep === LOADING && <FullPageLoader />}
          {activeStep === ENROLL_ERROR && <SendMoneyLimitsError enrollErrorData={enrollErrorData} />}
          {activeStep === INTRO && (
            <SendMoneyIntro
              isFetchingLinkedAccounts={isLoadingLinkedAccounts}
              handlePlaidMetaData={handlePlaidMetaData}
            />
          )}
          {activeStep === ACCOUNTS && (
            <PlaidLinkedAccounts
              onSubmit={setSelectedLinkedAccount}
              preSelectedLinkedAccount={selectedLinkedAccount}
              isLinkedAccountsLoading={isLoadingLinkedAccounts}
              linkedAccounts={updatedLinkedAccounts?.accounts}
              handlePlaidMetaData={handlePlaidMetaData}
              reFetchLinkedAccounts={() => getUpdatedLinkedAccounts()}
            />
          )}
          {activeStep === AMOUNT && (
            <TransferAmountPage onSubmit={handleAmountSubmit} subtitle={getPayeeDetailsText(selectedLinkedAccount)} />
          )}
          {activeStep === REVIEW && (
            <TransferReviewPage
              onSubmit={sendMoney}
              onEditAmount={handleEditAmountClick}
              onEditPayee={handleEditPayeeClick}
            />
          )}
          {activeStep === SUCCESS && (
            <TransferSuccessPage
              onMakeAnotherTransferClick={handleMakeAnotherTransferClick}
              rowsData={buildSuccessBoxTransferData(
                getRowTitlesForSuccessPage(t),
                selectedLinkedAccount,
                chosenFee,
                amount,
              )}
              processTime={sendMoneyResponse.processTime}
            />
          )}
        </div>
      </SendMoneyContext.Provider>
    </Layout>
  );
};
